import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getVacations, createVacation, approveVacation, rejectVacation, deleteVacation, getVacationCalendar } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { CalendarDays, Plus, Check, X, Trash2, Loader2, Palmtree, Users, RefreshCw } from 'lucide-react';
import { de } from 'date-fns/locale';

export const VacationPlannerPage = () => {
    const { user, isAdmin } = useAuth();
    const [vacations, setVacations] = useState([]);
    const [allVacations, setAllVacations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    
    const [formData, setFormData] = useState({
        start_date: '',
        end_date: '',
        vacation_type: 'VACATION',
        notes: ''
    });

    const loadData = useCallback(async () => {
        try {
            const [myVacationsRes, calendarRes] = await Promise.all([
                getVacations(),
                getVacationCalendar()
            ]);
            setVacations(myVacationsRes.data);
            setAllVacations(calendarRes.data);
        } catch (err) {
            toast.error('Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleRefresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            await loadData();
            toast.success('Aktualisiert');
        } catch (err) {
            toast.error('Fehler beim Aktualisieren');
        } finally {
            setRefreshing(false);
        }
    };

    const openDialog = () => {
        const today = new Date();
        setFormData({
            start_date: today.toISOString().split('T')[0],
            end_date: today.toISOString().split('T')[0],
            vacation_type: 'VACATION',
            notes: ''
        });
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.end_date < formData.start_date) {
            toast.error('Enddatum muss nach Startdatum liegen');
            return;
        }
        setSaving(true);
        try {
            await createVacation(formData);
            toast.success('Urlaubsantrag eingereicht');
            setDialogOpen(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async (id) => {
        try {
            await approveVacation(id);
            toast.success('Urlaub genehmigt');
            loadData();
        } catch (err) {
            toast.error('Fehler beim Genehmigen');
        }
    };

    const handleReject = async (id) => {
        try {
            await rejectVacation(id);
            toast.success('Urlaub abgelehnt');
            loadData();
        } catch (err) {
            toast.error('Fehler beim Ablehnen');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('Urlaubsantrag wirklich löschen?')) return;
        try {
            await deleteVacation(id);
            toast.success('Urlaubsantrag gelöscht');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
        }
    };

    // Get vacation dates for calendar highlighting
    const getVacationDates = () => {
        const dates = [];
        allVacations.forEach(v => {
            const start = new Date(v.start_date);
            const end = new Date(v.end_date);
            let current = new Date(start);
            while (current <= end) {
                dates.push(new Date(current));
                current.setDate(current.getDate() + 1);
            }
        });
        return dates;
    };

    // Get vacations for selected date
    const getVacationsForDate = (date) => {
        if (!date) return [];
        const dateStr = date.toISOString().split('T')[0];
        return allVacations.filter(v => {
            return dateStr >= v.start_date && dateStr <= v.end_date;
        });
    };

    const statusLabels = {
        'PENDING': { label: 'Ausstehend', class: 'bg-amber-100 text-amber-700' },
        'APPROVED': { label: 'Genehmigt', class: 'bg-emerald-100 text-emerald-700' },
        'REJECTED': { label: 'Abgelehnt', class: 'bg-red-100 text-red-700' }
    };

    const typeLabels = {
        'VACATION': 'Urlaub',
        'SICK': 'Krankheit',
        'OTHER': 'Sonstiges'
    };

    const selectedDateVacations = getVacationsForDate(selectedDate);
    const vacationDates = getVacationDates();
    
    // Memoize calendar modifiers to prevent unnecessary re-renders
    const calendarModifiers = useMemo(() => ({
        vacation: vacationDates
    }), [vacationDates]);
    
    const calendarModifiersClassNames = useMemo(() => ({
        vacation: 'bg-blue-500 text-white font-bold'
    }), []);

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-6 pb-24" data-testid="vacation-planner-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                        Urlaubsplaner
                        <button 
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="p-2 rounded-full hover:bg-muted transition-colors"
                            title="Aktualisieren"
                        >
                            <RefreshCw size={20} className={`text-muted-foreground ${refreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </h1>
                    <p className="text-muted-foreground">Urlaub planen und verwalten</p>
                </div>
                <Button onClick={openDialog} data-testid="new-vacation-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Urlaub beantragen
                </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Calendar */}
                <Card className="lg:col-span-1">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <CalendarDays className="h-5 w-5" />
                            Urlaubskalender
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={setSelectedDate}
                            locale={de}
                            className="rounded-md"
                            modifiers={calendarModifiers}
                            modifiersClassNames={calendarModifiersClassNames}
                        />
                        <div className="mt-4 pt-4 border-t">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <div className="h-4 w-4 rounded bg-blue-500" />
                                <span>Genehmigter Urlaub</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Selected Date Info */}
                <Card className="lg:col-span-2">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg">
                            {selectedDate ? selectedDate.toLocaleDateString('de-DE', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                            }) : 'Datum auswählen'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {selectedDateVacations.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <Palmtree className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                <p>Kein Urlaub an diesem Tag</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {selectedDateVacations.map(v => (
                                    <div key={v.id} className="p-4 bg-blue-50 border border-blue-200 rounded-sm">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Users className="h-4 w-4 text-blue-600" />
                                            <span className="font-semibold">{v.user_name}</span>
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {new Date(v.start_date).toLocaleDateString('de-DE')} - {new Date(v.end_date).toLocaleDateString('de-DE')}
                                        </p>
                                        <p className="text-sm text-muted-foreground">{v.days_count} Tage</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* My Vacation Requests */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Palmtree className="h-5 w-5" />
                        {isAdmin() ? 'Alle Urlaubsanträge' : 'Meine Urlaubsanträge'}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {vacations.length === 0 ? (
                        <p className="text-center py-8 text-muted-foreground">Keine Urlaubsanträge vorhanden</p>
                    ) : (
                        <div className="space-y-3">
                            {vacations.map(vacation => (
                                <div 
                                    key={vacation.id}
                                    className="p-4 border rounded-sm flex flex-col md:flex-row md:items-center justify-between gap-4"
                                >
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            {isAdmin() && (
                                                <span className="font-semibold">{vacation.user_name}</span>
                                            )}
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${statusLabels[vacation.status].class}`}>
                                                {statusLabels[vacation.status].label}
                                            </span>
                                            <span className="text-sm text-muted-foreground">
                                                {typeLabels[vacation.vacation_type]}
                                            </span>
                                        </div>
                                        <p className="font-medium">
                                            {new Date(vacation.start_date).toLocaleDateString('de-DE')} - {new Date(vacation.end_date).toLocaleDateString('de-DE')}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            {vacation.days_count} Arbeitstage
                                            {vacation.notes && ` • ${vacation.notes}`}
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        {isAdmin() && vacation.status === 'PENDING' && (
                                            <>
                                                <Button 
                                                    size="sm" 
                                                    className="bg-emerald-600 hover:bg-emerald-700"
                                                    onClick={() => handleApprove(vacation.id)}
                                                    data-testid={`approve-${vacation.id}`}
                                                >
                                                    <Check className="h-4 w-4 mr-1" />
                                                    Genehmigen
                                                </Button>
                                                <Button 
                                                    size="sm" 
                                                    variant="outline"
                                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                                    onClick={() => handleReject(vacation.id)}
                                                    data-testid={`reject-${vacation.id}`}
                                                >
                                                    <X className="h-4 w-4 mr-1" />
                                                    Ablehnen
                                                </Button>
                                            </>
                                        )}
                                        {vacation.status !== 'APPROVED' && (
                                            <Button 
                                                size="sm" 
                                                variant="ghost"
                                                onClick={() => handleDelete(vacation.id)}
                                            >
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* New Vacation Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="vacation-dialog">
                    <DialogHeader>
                        <DialogTitle>Urlaub beantragen</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Typ</Label>
                            <Select value={formData.vacation_type} onValueChange={(v) => setFormData({...formData, vacation_type: v})}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="VACATION">Urlaub</SelectItem>
                                    <SelectItem value="SICK">Krankheit</SelectItem>
                                    <SelectItem value="OTHER">Sonstiges</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Von</Label>
                                <Input
                                    type="date"
                                    value={formData.start_date}
                                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Bis</Label>
                                <Input
                                    type="date"
                                    value={formData.end_date}
                                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                                    required
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Anmerkungen</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                placeholder="Optional"
                                rows={2}
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit" disabled={saving}>
                                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                Beantragen
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default VacationPlannerPage;
